import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, ArrowLeft, ShieldCheck, LogOut } from "lucide-react";
import { toast } from "sonner";
import { BL_TOKEN_KEY, BL_ADMIN_TOKEN_KEY, MAIN_TOKEN_KEY, fetchBLAdmin } from "@/pages/blackLeaf/client";
import { BLLogin, BLLogo } from "@/pages/blackLeaf/BLLogin";
import { BLDashboard } from "@/pages/blackLeaf/BLDashboard";
import { BLNotices } from "@/pages/blackLeaf/BLNotices";
import { BLCatalog } from "@/pages/blackLeaf/BLCatalog";
import { BLListings } from "@/pages/blackLeaf/BLListings";
import { BLAdmins } from "@/pages/blackLeaf/BLAdmins";
import "@/styles/blackLeaf.css";

export default function BlackLeafSociety() {
  const [token, setToken] = useState(() => localStorage.getItem(BL_TOKEN_KEY));
  if (!token) return <BLLogin onAuth={(t) => { localStorage.setItem(BL_TOKEN_KEY, t); setToken(t); }} />;
  return <Shell onLogout={() => { localStorage.removeItem(BL_TOKEN_KEY); setToken(null); }} />;
}

const VIEWS = ["dashboard", "notices", "catalog", "seeking", "sale"];

function Shell({ onLogout }) {
  const navigate = useNavigate();
  const [view, setView] = useState("dashboard");
  const [admin, setAdmin] = useState(null);

  const refreshAdmin = () => fetchBLAdmin().then(setAdmin);
  useEffect(() => { refreshAdmin(); }, []);
  const isAdmin = !!admin;

  const onCrossover = () => {
    localStorage.setItem("bk_theme", "blackleaf-dark");
    if (localStorage.getItem(MAIN_TOKEN_KEY)) {
      navigate("/home");
    } else {
      navigate("/auth?theme=blackleaf-dark&from=blackleaf");
    }
  };

  const signOutAdmin = () => {
    localStorage.removeItem(BL_ADMIN_TOKEN_KEY);
    setAdmin(null);
    setView("dashboard");
    toast.success("Admin signed out");
  };

  const navItems = isAdmin ? [...VIEWS, "admins"] : VIEWS;

  return (
    <div className="black-leaf-theme min-h-screen pb-16" data-testid="bl-board">
      <header className="bl-topbar sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <button onClick={() => setView("dashboard")} className="text-left" data-testid="bl-home">
            <BLLogo size="sm" />
          </button>
          <nav className="hidden md:flex gap-1" data-testid="bl-nav">
            {navItems.map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`bl-nav-pill ${view === v ? "is-on" : ""}`}
                data-testid={`bl-nav-${v}`}
              >
                {labelOf(v)}
              </button>
            ))}
          </nav>
          <div className="flex gap-2 items-center">
            {isAdmin && (
              <span className="bl-admin-chip" title={`Signed in as ${admin.email}`} data-testid="bl-admin-chip">
                <ShieldCheck size={12}/> {admin.name || "Admin"}
              </span>
            )}
            {isAdmin && (
              <button onClick={signOutAdmin} className="bl-btn-ghost" data-testid="bl-admin-signout" title="Sign out of admin"><LogOut size={12}/></button>
            )}
            <Link to="/" className="bl-btn-ghost" data-testid="bl-back-main"><ArrowLeft size={12}/> Welcome</Link>
            <button onClick={onLogout} className="bl-btn-ghost" data-testid="bl-logout">Leave</button>
          </div>
        </div>
        {/* Mobile nav row */}
        <div className="md:hidden border-t border-bl-line overflow-x-auto">
          <div className="flex gap-1 px-3 py-2 min-w-max">
            {navItems.map((v) => (
              <button key={v} onClick={() => setView(v)} className={`bl-nav-pill ${view === v ? "is-on" : ""}`}>
                {labelOf(v)}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {view === "dashboard" && <BLDashboard onNavigate={setView} onCrossover={onCrossover} isAdmin={isAdmin} onGoAdmins={() => setView("admins")}/>}
        {view === "notices" && <BLNotices isAdmin={isAdmin}/>}
        {view === "catalog" && <BLCatalog isAdmin={isAdmin}/>}
        {view === "seeking" && <BLListings kind="seeking" isAdmin={isAdmin}/>}
        {view === "sale" && <BLListings kind="sales" isAdmin={isAdmin}/>}
        {view === "admins" && isAdmin && <BLAdmins me={admin} onChange={refreshAdmin}/>}
      </main>

      {view !== "dashboard" && (
        <button onClick={() => setView("dashboard")} className="bl-fab" aria-label="Dashboard" data-testid="bl-fab-dashboard">
          <LayoutDashboard size={18}/>
        </button>
      )}
    </div>
  );
}

function labelOf(v) {
  return {
    dashboard: "Dashboard",
    notices:   "Notices",
    catalog:   "Catalogue",
    seeking:   "Seeking",
    sale:      "For Sale",
    admins:    "Admins",
  }[v];
}
